'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoFocus", "disabled", "error", "inputComponent", "inputRef", "inputValue", "onInputValueChange", "onOpenChange", "onPaste", "placeholder", "readOnly", "selectComponent", "selectRef", "tabIndex", "value"];
import PropTypes from 'prop-types';
import { Component } from 'react';
import Controllable from '../../decorators/Controllable';
import { callIfPossible } from '../../core/Functions';
import SyntheticEvent from '../../core/SyntheticEvent';
import { isPageInKeyboardMode } from '../../listeners/focusStylesListener';
import { getAriaAndDataProps } from '../../utils/Props';
import refObject from '../../utils/propTypes/refObject';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class UISelectWithInputAnchor extends Component {
  constructor(...args) {
    super(...args);
    this.focus = () => {
      if (this._inputRef) {
        this._inputRef.focus();
      }
    };
    this.selectRefCallback = ref => {
      const {
        selectRef
      } = this.props;
      this._selectRef = ref;
      if (selectRef) {
        selectRef(ref);
      }
    };
    this.handleInputKeyDown = evt => {
      const {
        readOnly
      } = this.props;
      const internalSelect = this.getInternalSelect();
      if (internalSelect && !readOnly) {
        internalSelect.handleKeyDown(evt);
      }
    };
    this.handleInputChange = evt => {
      const {
        onInputValueChange
      } = this.props;
      onInputValueChange(evt);
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        internalSelect.handleInputChange(evt);
      }
    };
    this.handleInputFocus = () => {
      const {
        onOpenChange,
        readOnly
      } = this.props;
      const internalSelect = this.getInternalSelect();
      if (readOnly) return;
      if (internalSelect) {
        internalSelect.setState({
          isOpen: true
        });
      }
      callIfPossible(onOpenChange, SyntheticEvent(true));
    };
    this.handleInputBlur = () => {
      const {
        onOpenChange
      } = this.props;
      if (!isPageInKeyboardMode()) return;
      callIfPossible(onOpenChange, SyntheticEvent(false));
    };
  }
  getInternalSelect() {
    // Return our react-select component instance, or null
    if (!this._selectRef) {
      return null;
    }
    if (!this._selectRef.select) {
      return this._selectRef;
    }
    // _selectRef must be an <Async> or <Creatable> wrapper
    return this._selectRef.select;
  }
  getInternalAsyncSelect() {
    // Return our react-select Async instance, or null
    if (!this._selectRef || !this._selectRef.select) {
      return null;
    }
    return this._selectRef;
  }
  render() {
    const _this$props = this.props,
      {
        autoFocus,
        disabled,
        error,
        inputComponent,
        inputRef,
        inputValue,
        onPaste,
        placeholder,
        readOnly,
        selectComponent,
        tabIndex,
        value
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const InputComponent = inputComponent;
    const SelectComponent = selectComponent;
    const ariaAndDataProps = getAriaAndDataProps(rest);
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(InputComponent, Object.assign({}, ariaAndDataProps, {
        autoFocus: autoFocus,
        disabled: disabled,
        "aria-invalid": error,
        inputRef: inputRef,
        onBlur: this.handleInputBlur,
        onChange: this.handleInputChange,
        onFocus: this.handleInputFocus,
        onKeyDown: this.handleInputKeyDown,
        onPaste: onPaste,
        placeholder: placeholder,
        readOnly: readOnly,
        ref: ref => {
          this._inputRef = ref;
        },
        required: ariaAndDataProps['aria-required'],
        tabIndex: tabIndex,
        value: inputValue
      })), /*#__PURE__*/_jsx(SelectComponent, Object.assign({}, rest, {
        autosize: false,
        className: "hidden",
        disabled: disabled,
        ref: this.selectRefCallback,
        scrollMenuIntoView: false,
        value: value
      }))]
    });
  }
}
UISelectWithInputAnchor.propTypes = {
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  menuWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
  inputComponent: PropTypes.func.isRequired,
  inputRef: refObject,
  isOpen: PropTypes.bool.isRequired,
  onInputValueChange: PropTypes.func,
  onOpenChange: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  searchClassName: PropTypes.string,
  selectComponent: PropTypes.func.isRequired,
  selectRef: PropTypes.func,
  inputValue: PropTypes.string,
  value: PropTypes.any
};
UISelectWithInputAnchor.displayName = 'UISelectWithInputAnchor';
export default Controllable(UISelectWithInputAnchor, ['inputValue']);